import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Typography, Alert, LinearProgress, Divider, Chip, Box, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import { ReceiptLong, Download, CheckCircle } from '@mui/icons-material';
import api from '../../services/api';
import type { InvoicePreview, FiscalInvoice } from '../../types';

interface Props {
  open: boolean;
  orderIds: number[];
  onClose: () => void;
  onEmitted?: (inv: FiscalInvoice) => void;
}

const CBTE_TIPOS = [
  { value: 1, label: 'Factura A' },
  { value: 6, label: 'Factura B' },
  { value: 11, label: 'Factura C' },
];
const DOC_TIPOS = [
  { value: 99, label: 'Consumidor Final (sin documento)' },
  { value: 80, label: 'CUIT' },
  { value: 96, label: 'DNI' },
];
const COND_IVA_RECEPTOR = [
  { value: 5, label: 'Consumidor Final' },
  { value: 1, label: 'Responsable Inscripto' },
  { value: 6, label: 'Monotributo' },
  { value: 4, label: 'Exento' },
];

const fmt = (v: number) => `$${(v ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InvoiceDialog: React.FC<Props> = ({ open, orderIds, onClose, onEmitted }) => {
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FiscalInvoice | null>(null);

  // Campos editables
  const [cbteTipo, setCbteTipo] = useState(6);
  const [docTipo, setDocTipo] = useState(99);
  const [docNro, setDocNro] = useState('');
  const [condIva, setCondIva] = useState(5);

  const load = useCallback(async () => {
    if (!orderIds.length) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const p = await api.previewInvoice(orderIds);
      setPreview(p);
      setCbteTipo(p.suggestedCbteTipo);
      setDocTipo(p.docTipo);
      setDocNro(p.docNro ? String(p.docNro) : '');
      setCondIva(p.condicionIvaReceptorId);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al calcular el comprobante');
    } finally {
      setLoading(false);
    }
  }, [orderIds]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const emit = async () => {
    setEmitting(true); setError(null);
    try {
      const inv = await api.emitInvoice({
        orderIds,
        cbteTipo,
        concepto: preview?.concepto || 1,
        docTipo,
        docNro: docTipo === 99 ? 0 : Number(docNro.replace(/\D/g, '')) || 0,
        condicionIvaReceptorId: condIva,
      });
      setResult(inv);
      onEmitted?.(inv);
    } catch (e: any) {
      const data = e?.response?.data;
      // El backend devuelve { message, invoice } cuando ARCA rechaza.
      setError(data?.message || 'Error al emitir el comprobante');
      if (data?.invoice) setResult(data.invoice);
    } finally {
      setEmitting(false);
    }
  };

  const downloadPdf = async () => {
    if (!result) return;
    try {
      const blob = await api.downloadFiscalInvoicePdf(result.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comprobante-${String(result.ptoVta).padStart(5, '0')}-${String(result.cbteNro).padStart(8, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al descargar el PDF');
    }
  };

  const authorized = result?.status === 'Authorized';
  const needsDocNro = docTipo !== 99;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReceiptLong color="primary" />
          <span>Facturar {orderIds.length > 1 ? `${orderIds.length} ventas` : 'venta'}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {authorized && result && (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
            Comprobante autorizado. CAE <b>{result.cae}</b> (vence {result.caeFchVto ? new Date(result.caeFchVto).toLocaleDateString() : '—'}).
            N° {String(result.ptoVta).padStart(5, '0')}-{String(result.cbteNro).padStart(8, '0')}.
          </Alert>
        )}

        {preview && !authorized && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip label={preview.customerName || 'Consumidor final'} />
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" color="text.secondary">Neto {fmt(preview.impNeto)}</Typography>
              <Typography variant="body2" color="text.secondary">IVA {fmt(preview.impIVA)}</Typography>
              <Typography variant="subtitle1" fontWeight={700}>Total {fmt(preview.impTotal)}</Typography>
            </Stack>

            {preview.orders.some(o => o.alreadyInvoiced) && (
              <Alert severity="warning">Alguna de las ventas ya fue facturada.</Alert>
            )}
            {preview.warnings.map((w, i) => <Alert key={i} severity="warning">{w}</Alert>)}

            <Divider />

            <TextField select label="Tipo de comprobante" size="small" value={cbteTipo}
              onChange={e => setCbteTipo(Number(e.target.value))}>
              {CBTE_TIPOS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Tipo de documento" size="small" fullWidth value={docTipo}
                onChange={e => { const v = Number(e.target.value); setDocTipo(v); if (v === 99) { setDocNro(''); setCondIva(5); } }}>
                {DOC_TIPOS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField label="N° de documento" size="small" fullWidth
                value={docTipo === 99 ? '' : docNro}
                disabled={!needsDocNro}
                onChange={e => setDocNro(e.target.value)}
                error={!!preview.missingFields.includes('docNro') && needsDocNro && !docNro}
                helperText={needsDocNro && !docNro ? 'Requerido' : ' '}
              />
            </Stack>

            <TextField select label="Condición IVA del receptor" size="small" value={condIva}
              onChange={e => setCondIva(Number(e.target.value))}>
              {COND_IVA_RECEPTOR.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>

            <Table size="small">
              <TableBody>
                {preview.orders.map(o => (
                  <TableRow key={o.id}>
                    <TableCell sx={{ borderBottom: 'none', py: 0.25 }}>
                      <Typography variant="caption">{o.reference}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: 'none', py: 0.25 }}>
                      <Typography variant="caption">{fmt(o.total)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {authorized
          ? <Button variant="contained" startIcon={<Download />} onClick={downloadPdf}>Descargar PDF</Button>
          : <Button variant="contained" onClick={emit}
              disabled={emitting || loading || !preview || (needsDocNro && !docNro)}>
              {emitting ? 'Emitiendo…' : 'Emitir comprobante'}
            </Button>}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDialog;
